import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Description, Download, Folder, Visibility } from '@mui/icons-material';

interface ShareDocumentMeta {
  kind?: 'document';
  title: string;
  originalName?: string;
  mimeType?: string;
  fileSize?: number;
  expiresAt?: string;
}

interface ShareFolderMeta {
  kind: 'folder';
  folder: { id: string; name: string; path?: string };
  expiresAt?: string;
  folders: { id: string; name: string; path?: string }[];
  documents: {
    id: string;
    title: string;
    originalName?: string;
    mimeType?: string;
    fileSize?: number;
  }[];
}

type ShareMeta = ShareDocumentMeta | ShareFolderMeta;

const formatBytes = (n?: number) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const PublicSharePage: React.FC = () => {
  const { token = '' } = useParams();
  const [meta, setMeta] = useState<ShareMeta | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/public/drive/s/${token}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Share unavailable');
        setMeta(json.data);
        setError('');
      } catch (err: any) {
        setError(err.message || 'Share unavailable');
        setMeta(null);
      } finally {
        setLoading(false);
      }
    };
    if (token) void load();
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !meta) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error">{error || 'Share not found'}</Alert>
      </Box>
    );
  }

  if (meta.kind === 'folder') {
    const folderMeta = meta as ShareFolderMeta;
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 8, px: 2 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Folder color="primary" /> {folderMeta.folder.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shared folder
            {folderMeta.expiresAt
              ? ` · Expires ${new Date(folderMeta.expiresAt).toLocaleString()}`
              : ''}
          </Typography>

          {(folderMeta.folders || []).length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                Subfolders
              </Typography>
              <List dense>
                {folderMeta.folders.map((f) => (
                  <ListItem key={f.id}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Folder fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={f.name} secondary={f.path} />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Files
          </Typography>
          <List dense>
            {(folderMeta.documents || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                No files in this folder.
              </Typography>
            ) : (
              folderMeta.documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        href={`/api/public/drive/s/${token}/files/${doc.id}/preview`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        href={`/api/public/drive/s/${token}/files/${doc.id}/download`}
                      >
                        Download
                      </Button>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Description fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title}
                    secondary={`${doc.originalName || ''} ${formatBytes(doc.fileSize)}`.trim()}
                    sx={{ pr: 28 }}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      </Box>
    );
  }

  const docMeta = meta as ShareDocumentMeta;
  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 8, px: 2 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {docMeta.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {docMeta.originalName}
          {docMeta.expiresAt ? ` · Expires ${new Date(docMeta.expiresAt).toLocaleString()}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Visibility />}
            href={`/api/public/drive/s/${token}/preview`}
            target="_blank"
            rel="noreferrer"
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            href={`/api/public/drive/s/${token}/download`}
          >
            Download
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PublicSharePage;
